"use client";

import React, { useState } from "react";

import { Chip } from "@heroui/chip";
import { Calendar, MapPin, Package, Truck } from "lucide-react";

import { HelpfulLinkPreview } from "./HelpfulLinkPreview";

import { ClaimDetail } from "@/components/admin/needs/ClaimDetail";
import { NeedImage } from "@/components/needs/NeedImage";
import { DateDisplay } from "@/components/ui/DateDisplay";
import { useToast } from "@/hooks/useToast";
import {
  markClaimCompletedAction,
  unclaimNeedAction,
} from "@/lib/actions/claims";
import {
  capitalizeFirstLetter,
  NeedUrgency,
  UrgencyColors,
} from "@/lib/types/common";

interface NeedDetailsLayoutProps {
  need: {
    id: string;
    title: string;
    description: string;
    status: string;
    urgency: string;
    imageUrl?: string | null;
    area: { name: string; state?: string };
    category?: { name: string; id: string } | null;
    tags?: { id: string; name: string }[];
    createdAt: Date;
    dueDate?: Date | null;
    targetQuantity?: number | null;
    needType?: string;
    dropoffLocation?: string | null;
    shippingAddress?: string | null;
    impactDescription?: string | null;
    helpfulLinks?: string[];
    organizationId: string;
    claims?: {
      id: string;
      claimToken: string;
      status: string;
      quantity: number;
      claimedAt: Date;
      unclaimedAt?: Date | null;
      volunteer: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string | null;
      };
    }[];
  };
  sidebarContent: React.ReactNode;
  className?: string;
}

const statusColorMap: Record<
  string,
  "success" | "warning" | "danger" | "default" | "primary"
> = {
  cancelled: "danger",
  claimed: "primary",
  completed: "success",
  expired: "danger",
  open: "warning",
};

const needTypeLabels: Record<string, string> = {
  FINANCIAL_ASSISTANCE: "Financial Assistance",
  PEOPLE_NEEDED: "People Needed",
  PHYSICAL_ITEM: "Physical Item",
};

const needTypeColors: Record<string, "primary" | "success" | "secondary"> = {
  FINANCIAL_ASSISTANCE: "success",
  PEOPLE_NEEDED: "secondary",
  PHYSICAL_ITEM: "primary",
};

function getNeedTypeLabel(needType: string): string {
  return needTypeLabels[needType] ?? "Physical Item";
}

function getNeedTypeColor(
  needType: string,
): "primary" | "success" | "secondary" {
  return needTypeColors[needType] ?? "primary";
}

function formatQuantityDisplay(
  needType: string | undefined,
  targetQuantity: number,
): string {
  if (needType === "FINANCIAL_ASSISTANCE") {
    return `$${(targetQuantity / 100).toFixed(2)}`;
  }
  if (needType === "PEOPLE_NEEDED") {
    return `${targetQuantity} people`;
  }

  return `${targetQuantity}`;
}

function getQuantityLabel(needType: string | undefined): string {
  if (needType === "FINANCIAL_ASSISTANCE") return "Amount Needed";
  if (needType === "PEOPLE_NEEDED") return "People Needed";

  return "Items Needed";
}

type ClaimRecord = NonNullable<
  NeedDetailsLayoutProps["need"]["claims"]
>[number];
type SetClaims = React.Dispatch<React.SetStateAction<ClaimRecord[]>>;
type SetLoading = React.Dispatch<React.SetStateAction<Set<string>>>;
type ToastFn = ReturnType<typeof useToast>;

function removeLoadingClaim(setLoadingClaims: SetLoading, claimToken: string) {
  setLoadingClaims((prev) => {
    const newSet = new Set(prev);

    newSet.delete(claimToken);

    return newSet;
  });
}

async function performMarkCompleted(
  claimToken: string,
  optimisticClaims: ClaimRecord[],
  setOptimisticClaims: SetClaims,
  setLoadingClaims: SetLoading,
  toast: ToastFn,
) {
  const claimIndex = optimisticClaims.findIndex(
    (c) => c.claimToken === claimToken,
  );

  if (claimIndex === -1) return;

  const originalClaims = [...optimisticClaims];

  setOptimisticClaims((prev) =>
    prev.map((claim) =>
      claim.claimToken === claimToken
        ? { ...claim, status: "completed" as const }
        : claim,
    ),
  );
  setLoadingClaims((prev) => new Set(prev).add(claimToken));

  try {
    const result = await markClaimCompletedAction(claimToken);

    if ("error" in result) {
      setOptimisticClaims(originalClaims);
      toast.error("Failed to mark claim as completed", {
        description: result.error,
      });

      return;
    }

    toast.success("Claim marked as completed");
  } catch {
    setOptimisticClaims(originalClaims);
    toast.error("Failed to mark claim as completed", {
      description: "An unexpected error occurred",
    });
  } finally {
    removeLoadingClaim(setLoadingClaims, claimToken);
  }
}

async function performUnclaim(
  claimToken: string,
  optimisticClaims: ClaimRecord[],
  setOptimisticClaims: SetClaims,
  setLoadingClaims: SetLoading,
  toast: ToastFn,
) {
  const claimIndex = optimisticClaims.findIndex(
    (c) => c.claimToken === claimToken,
  );

  if (claimIndex === -1) return;

  const originalClaims = [...optimisticClaims];

  setOptimisticClaims((prev) =>
    prev.map((claim) =>
      claim.claimToken === claimToken
        ? { ...claim, status: "canceled" as const, unclaimedAt: new Date() }
        : claim,
    ),
  );
  setLoadingClaims((prev) => new Set(prev).add(claimToken));

  try {
    const result = await unclaimNeedAction(claimToken);

    if ("error" in result) {
      setOptimisticClaims(originalClaims);
      toast.error("Failed to unclaim", { description: result.error });

      return;
    }

    toast.success("Claim canceled successfully");
  } catch {
    setOptimisticClaims(originalClaims);
    toast.error("Failed to unclaim", {
      description: "An unexpected error occurred",
    });
  } finally {
    removeLoadingClaim(setLoadingClaims, claimToken);
  }
}

type NeedProp = NeedDetailsLayoutProps["need"];

function renderNeedSidebar(need: NeedProp, sidebarContent: React.ReactNode) {
  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-default-200 overflow-hidden">
        <NeedImage
          alt={need.title}
          need={{
            tags: need.tags,
            urgency: need.urgency as NeedUrgency,
          }}
          src={need.imageUrl}
        />
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap gap-2">
            <Chip
              aria-label={`Status: ${capitalizeFirstLetter(need.status)}`}
              color={statusColorMap[need.status]}
              size="md"
              variant="flat"
            >
              {capitalizeFirstLetter(need.status)}
            </Chip>
            <Chip
              aria-label={`Urgency: ${capitalizeFirstLetter(need.urgency)}`}
              color={UrgencyColors[need.urgency as NeedUrgency]}
              size="md"
              variant="flat"
            >
              {capitalizeFirstLetter(need.urgency)} Urgency
            </Chip>
            {need.category && (
              <Chip
                aria-label={`Category: ${need.category.name}`}
                color="secondary"
                size="md"
                variant="flat"
              >
                {need.category.name}
              </Chip>
            )}
            {need.needType && (
              <Chip
                aria-label={`Type: ${getNeedTypeLabel(need.needType)}`}
                color={getNeedTypeColor(need.needType)}
                data-testid="need-type-chip"
                size="md"
                variant="flat"
              >
                {getNeedTypeLabel(need.needType)}
              </Chip>
            )}
            {need.tags && need.tags.length > 0 && (
              <>
                {need.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="text-sm bg-default-100 text-default-700 px-3 py-1 rounded-full"
                  >
                    {tag.name}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="hidden lg:block lg:mt-4">{sidebarContent}</div>
    </div>
  );
}

function formatAreaDisplay(area: NeedProp["area"]): string {
  return area.state ? `${area.name}, ${area.state}` : area.name;
}

function renderNeedDetails(need: NeedProp) {
  const areaDisplay = formatAreaDisplay(need.area);
  const hasQuantity = need.targetQuantity != null;

  return (
    <div className="bg-default-50 p-4 rounded-lg border border-default-200 space-y-3">
      <h3 className="text-lg font-semibold mb-4">Details</h3>
      <div className="flex items-center gap-3">
        <MapPin className="h-4 w-4 text-default-400" />
        <div>
          <div className="text-sm font-medium">Area</div>
          <div className="text-default-600">{areaDisplay}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Calendar className="h-4 w-4 text-default-400" />
        <div>
          <div className="text-sm font-medium">Posted</div>
          <div className="text-default-600">
            <DateDisplay
              ariaLabel="Date posted"
              date={need.createdAt}
              format="MMMM d, yyyy"
            />
          </div>
        </div>
      </div>
      {need.dueDate && (
        <div className="flex items-center gap-3">
          <Calendar className="h-4 w-4 text-default-400" />
          <div>
            <div className="text-sm font-medium">Due Date</div>
            <div className="text-default-600">
              <DateDisplay
                ariaLabel="Due date"
                date={need.dueDate}
                format="MMMM d, yyyy"
              />
            </div>
          </div>
        </div>
      )}
      {hasQuantity && (
        <div className="flex items-center gap-3">
          <Package className="h-4 w-4 text-default-400" />
          <div>
            <div className="text-sm font-medium">
              {getQuantityLabel(need.needType)}
            </div>
            <div className="text-default-600" data-testid="need-num-items">
              {formatQuantityDisplay(need.needType, need.targetQuantity!)}
            </div>
          </div>
        </div>
      )}
      {need.dropoffLocation && (
        <div className="flex items-center gap-3">
          <MapPin className="h-4 w-4 text-default-400" />
          <div>
            <div className="text-sm font-medium">Drop-off Location</div>
            <div className="text-default-600">{need.dropoffLocation}</div>
          </div>
        </div>
      )}
      {need.shippingAddress && (
        <div className="flex items-center gap-3">
          <Truck className="h-4 w-4 text-default-400" />
          <div>
            <div className="text-sm font-medium">Shipping Address</div>
            <div className="text-default-600">{need.shippingAddress}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderNeedMainContent(
  need: NeedProp,
  optimisticClaims: ClaimRecord[],
  loadingClaims: Set<string>,
  onMarkCompleted: (claimToken: string) => Promise<void>,
  onUnclaim: (claimToken: string) => Promise<void>,
  sidebarContent: React.ReactNode,
) {
  const validLinks = need.helpfulLinks
    ? need.helpfulLinks.filter((link) => link.trim().length > 0)
    : [];
  const hasClaims = optimisticClaims.length > 0;

  return (
    <div className="lg:col-span-2">
      <div className="bg-white rounded-xl shadow-sm border border-default-200 p-6">
        <h1 className="text-3xl font-bold text-default-900 break-words mb-2">
          {need.title}
        </h1>
        <p className="text-default-600 mb-4">{need.description}</p>
        {need.impactDescription && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-3">Who This Helps</h3>
            <p className="text-default-700 whitespace-pre-line">
              {need.impactDescription}
            </p>
          </div>
        )}
        {validLinks.length > 0 && (
          <div className="mb-4 mt-8">
            <h3 className="text-md font-semibold mb-3">Helpful Links</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {validLinks.map((link, index) => (
                <HelpfulLinkPreview key={index} url={link} />
              ))}
            </div>
          </div>
        )}
        {renderNeedDetails(need)}
      </div>
      {hasClaims && (
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-default-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Claims</h3>
            <div className="space-y-4">
              {optimisticClaims.map((claim) => (
                <ClaimDetail
                  key={claim.id}
                  claim={claim}
                  isLoading={loadingClaims.has(claim.claimToken)}
                  showActions={true}
                  onMarkCompleted={onMarkCompleted}
                  onUnclaim={onUnclaim}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="lg:hidden mt-6">{sidebarContent}</div>
    </div>
  );
}

export default function NeedDetailsLayout({
  className,
  need,
  sidebarContent,
}: NeedDetailsLayoutProps) {
  const resolvedClassName = className ?? "";
  const toast = useToast();
  const [optimisticClaims, setOptimisticClaims] = useState(need.claims ?? []);
  const [loadingClaims, setLoadingClaims] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    setOptimisticClaims(need.claims ?? []);
  }, [need.claims]);

  const handleMarkCompleted = (claimToken: string) =>
    performMarkCompleted(
      claimToken,
      optimisticClaims,
      setOptimisticClaims,
      setLoadingClaims,
      toast,
    );

  const handleUnclaim = (claimToken: string) =>
    performUnclaim(
      claimToken,
      optimisticClaims,
      setOptimisticClaims,
      setLoadingClaims,
      toast,
    );

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 ${resolvedClassName}`}
    >
      {renderNeedSidebar(need, sidebarContent)}
      {renderNeedMainContent(
        need,
        optimisticClaims,
        loadingClaims,
        handleMarkCompleted,
        handleUnclaim,
        sidebarContent,
      )}
    </div>
  );
}
